import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import * as spine from "@esotericsoftware/spine-webgl";
import { useConfigStore } from "../../store/configStore";
import { useEditorStore } from "../../store/editorStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, Eraser, Shuffle } from "lucide-react";
import { SpinePreviewApp } from "../../lib/SpinePreviewApp";
import {
  isStaticSpineTemplateType,
  type SpineTemplate,
} from "../../types/spine-template";
import { StaticLayerPreview } from "./StaticLayerPreview";
import { ProSkuCount } from "./ProSkuCount";
import {
  buildMergedStaticStackItems,
  zIndexForSpineCanvas,
  type StaticStackItem,
} from "../../lib/staticLayerStack";

const CHECKER_BG = `
                linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
                linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
                linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
                linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)
              `;

export function SpinePreview() {
  const { tid } = useParams<{ tid: string }>();
  const { data: template } = useSWR<SpineTemplate>(
    tid ? [`/spine-templates/${tid}`] : null,
  );
  const fullscreenType = template?.fullscreenType ?? 0;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const spineCanvasRef = useRef<spine.SpineCanvas | null>(null);
  const appRef = useRef<SpinePreviewApp | null>(null);

  const [error, setError] = useState<string | null>(null);

  const templateType = useConfigStore((s) => s.templateType);
  const templateConfig = useConfigStore((s) => s.templateConfig);
  const spineData = useConfigStore((s) => s.spineData);
  const loadedRelatedTemplates = useConfigStore(
    (s) => s.loadedRelatedTemplates,
  );
  const allRelatedTemplateIds = useConfigStore((s) => s.allRelatedTemplateIds);
  const randomizeSkins = useEditorStore((s) => s.randomizeSkins);
  const resetPreview = useEditorStore((s) => s.resetPreview);
  const setSpinePreview = useEditorStore((s) => s.setSpinePreview);
  const setPreviewMergedStaticStack = useEditorStore(
    (s) => s.setPreviewMergedStaticStack,
  );
  const selectedSkins = useEditorStore((s) => s.selectedSkins);

  const isStatic = isStaticSpineTemplateType(templateType);
  const hasRelated =
    Array.isArray(loadedRelatedTemplates) && loadedRelatedTemplates.length > 0;

  const relatedTemplateOrderIndexById = useMemo(() => {
    const m = new Map<string, number>();
    (allRelatedTemplateIds || []).forEach((id, i) => m.set(id, i));
    return m;
  }, [allRelatedTemplateIds]);

  const relatedStaticOnly = useMemo(() => {
    const filtered = (loadedRelatedTemplates || []).filter(
      (rel) =>
        !rel.spineData &&
        rel.config &&
        isStaticSpineTemplateType(rel.type),
    );
    return [...filtered].sort(
      (a, b) =>
        (relatedTemplateOrderIndexById.get(a.id) ?? 0) -
        (relatedTemplateOrderIndexById.get(b.id) ?? 0),
    );
  }, [loadedRelatedTemplates, relatedTemplateOrderIndexById]);

  /** 是否需要 WebGL：主 Zip 或任意已加载的关联 Spine */
  const needsSpineCanvas =
    !!spineData ||
    (loadedRelatedTemplates || []).some((r) => r.spineData);

  const mergedStaticStack = useMemo(
    () =>
      buildMergedStaticStackItems({
        mainTemplateType: templateType,
        mainTemplateConfig: isStatic ? templateConfig : null,
        relatedStatic: relatedStaticOnly.map((r) => ({
          id: r.id,
          type: r.type,
          config: r.config,
        })),
        selectedSkins,
        relatedTemplateOrderIndexById,
      }),
    [
      templateType,
      templateConfig,
      isStatic,
      relatedStaticOnly,
      selectedSkins,
      relatedTemplateOrderIndexById,
    ],
  );

  /** 静态图 + 可选 WebGL 画布，按 TEMPLATE_ORDER 同栈 */
  const previewStackItems = useMemo((): StaticStackItem[] => {
    const items: StaticStackItem[] = [...mergedStaticStack];
    if (needsSpineCanvas) {
      items.push({
        kind: "canvas",
        key: "spine-webgl",
        zIndex: zIndexForSpineCanvas(),
      });
    }
    return items.sort((a, b) => a.zIndex - b.zIndex);
  }, [mergedStaticStack, needsSpineCanvas]);

  /** 供导出动图等与预览共用同一套静态叠层，避免导出侧重复拼层 */
  useEffect(() => {
    setPreviewMergedStaticStack(mergedStaticStack);
    return () => setPreviewMergedStaticStack([]);
  }, [mergedStaticStack, setPreviewMergedStaticStack]);

  useEffect(() => {
    if (!needsSpineCanvas || !canvasRef.current) {
      setSpinePreview(null, null);
      return;
    }

    setError(null);

    const app = new SpinePreviewApp({
      spineData: spineData ?? null,
      relatedTemplates: [...(loadedRelatedTemplates || [])].sort(
        (a, b) =>
          (relatedTemplateOrderIndexById.get(a.id) ?? 0) -
          (relatedTemplateOrderIndexById.get(b.id) ?? 0),
      ),
      relatedTemplateIdsOrder: allRelatedTemplateIds || [],
      setError,
      fullscreenType,
    });
    appRef.current = app;

    try {
      const spineCanvas = new spine.SpineCanvas(canvasRef.current, {
        app: app,
        webglConfig: {
          alpha: true,
          preserveDrawingBuffer: true,
          premultipliedAlpha: false,
          antialias: false,
        },
      });
      spineCanvasRef.current = spineCanvas;
      setSpinePreview(app, spineCanvas);
    } catch (err) {
      console.error("创建 SpineCanvas 失败:", err);
      setError(err instanceof Error ? err.message : "创建 SpineCanvas 失败");
    }

    return () => {
      setSpinePreview(null, null);
      if (spineCanvasRef.current) {
        spineCanvasRef.current.dispose();
      }
      spineCanvasRef.current = null;
      appRef.current = null;
    };
  }, [
    spineData,
    loadedRelatedTemplates,
    needsSpineCanvas,
    setSpinePreview,
    allRelatedTemplateIds,
    relatedTemplateOrderIndexById,
    fullscreenType,
  ]);

  // 普通模版：未导入且无关联
  if (!isStatic && !spineData && !hasRelated) {
    return (
      <Card className="flex flex-col bg-gradient-to-br from-card to-muted/30">
        <CardContent className="aspect-square flex items-center justify-center text-center text-muted-foreground p-2">
          <div>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
              <AlertCircle className="h-8 w-8" />
            </div>
            <p>请先上传 Spine 文件</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // 静态主模版：无主 Zip、未勾选任何关联预览、且主模版无静态层可展示时
  if (isStatic && !needsSpineCanvas && mergedStaticStack.length === 0) {
    return <StaticLayerPreview />;
  }

  return (
    <div className="h-full flex flex-col gap-3 min-h-0">
      <Card className="flex flex-col flex-1 min-h-0">
        <CardHeader className="p-4 pl-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">预览</CardTitle>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                onClick={() => resetPreview()}
                title="清空预览（去掉已选皮肤、染色与动画）"
              >
                <Eraser className="h-4 w-4" />
              </Button>
              {needsSpineCanvas && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={randomizeSkins}
                  title="随机换装"
                >
                  <Shuffle className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0 flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto">
          {error ? (
            <div className="aspect-square flex flex-col items-center justify-center text-destructive text-sm gap-2">
              <AlertCircle className="h-8 w-8" />
              <p>{error}</p>
            </div>
          ) : (
            <div
              className="relative isolate z-0 w-full aspect-square rounded-md overflow-hidden bg-muted/40 border border-border shrink-0"
              style={{
                backgroundImage: CHECKER_BG,
                backgroundSize: "20px 20px",
                backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                backgroundColor: "#f5f5f5",
              }}
            >
              <ProSkuCount />
              {previewStackItems.map((item) =>
                item.kind === "img" ? (
                  <img
                    key={item.key}
                    src={item.url}
                    alt=""
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
                    style={{ zIndex: item.zIndex }}
                    draggable={false}
                  />
                ) : (
                  <canvas
                    key={item.key}
                    ref={canvasRef}
                    className="absolute inset-0 w-full h-full rounded-md bg-transparent"
                    style={{ zIndex: item.zIndex }}
                  />
                ),
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
